import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Focus & Pomodoro — focused work sessions as execution evidence.
 *
 * "Tasks define what. Calendar defines when. Focus sessions PROVE execution."
 * A FocusSession records a completed block of deep work (minutes), optionally
 * linked to the task/project it advanced. Totals are gentle evidence of effort,
 * never a surveillance clock.
 */

export interface FocusSession extends RecordEnvelope {
  readonly kind: 'focus_session';
  readonly minutes: number;
  /** ISO date the session is counted under. */
  readonly occurredOn: string;
  readonly note: string;
  /** Optional task/project this session advanced. */
  readonly linkedId: EntityId | null;
}

export interface FocusSessionInput {
  readonly minutes: number;
  readonly occurredOn?: string;
  readonly note?: string;
  readonly linkedId?: EntityId | null;
  readonly privacyClass?: PrivacyClass;
}

/** Common pomodoro lengths (minutes). UI hint only. */
export const FOCUS_PRESETS: readonly number[] = [15, 25, 50, 90] as const;

export interface FocusSummary {
  readonly totalMinutes: number;
  readonly sessionCount: number;
  readonly daysFocused: number;
}

const dayOf = (iso: string): string => iso.slice(0, 10);

/** Total focus minutes and session count over [fromIso, toIso] (inclusive). Pure. */
export function summarizeFocus(
  sessions: readonly Pick<FocusSession, 'minutes' | 'occurredOn'>[],
  fromIso: string,
  toIso: string,
): FocusSummary {
  const from = dayOf(fromIso);
  const to = dayOf(toIso);
  const days = new Set<string>();
  let totalMinutes = 0;
  let sessionCount = 0;
  for (const s of sessions) {
    const d = dayOf(s.occurredOn);
    if (d < from || d > to) continue;
    totalMinutes += Math.max(0, Math.trunc(s.minutes));
    sessionCount += 1;
    days.add(d);
  }
  return { totalMinutes, sessionCount, daysFocused: days.size };
}
